"use client"

import { useState } from "react"
import { HttpTypes } from "@medusajs/types"
import { ChevronRight, ChevronDown, Package } from "lucide-react"
import { useRouter, useSearchParams } from "next/navigation"
import { useT } from "../../../../i18n/client"
import { getLocalizedName } from "i18n/medusa-helpers"

type CategorySelectorProps = {
  categories: HttpTypes.StoreProductCategory[]
  locale: string
}

type CategoryItemProps = {
  category: HttpTypes.StoreProductCategory
  locale: string
  level?: number
}

const CategoryItem = ({ category, locale, level = 0, onCategorySelect, expandedId, onToggleExpand }: CategoryItemProps & { onCategorySelect: (id: string) => void, expandedId: string | null, onToggleExpand: (id: string) => void }) => {
  const hasChildren = category.category_children && category.category_children.length > 0
  const isExpanded = expandedId === category.id
  const categoryThumbnail = category.metadata?.thumbnail as string
  
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div 
        className={`flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer ${
          level > 0 ? `pl-${6 + level * 4} bg-gray-50` : ''
        }`}
      >
        <button 
          onClick={() => {
            onCategorySelect(category.id)
            if (hasChildren) onToggleExpand(category.id)
          }}
          className="flex items-center gap-2 flex-1 text-left"
        >
          {categoryThumbnail ? (
            <img 
              src={categoryThumbnail} 
              alt={getLocalizedName(category, locale)} 
              className="w-4 h-4 object-cover rounded"
            />
          ) : (
            <Package className="w-4 h-4 text-gray-500" />
          )}
          <span className="text-sm font-medium">{getLocalizedName(category, locale)}</span>
        </button>
        
        {hasChildren && (
          <button 
            onClick={() => onToggleExpand(category.id)}
            className="p-1 hover:bg-gray-200 rounded"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-600" />
            )}
          </button>
        )}
      </div>
      
      {hasChildren && isExpanded && (
        <div>
          {category.category_children
            .sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))
            .map((child) => (
            <CategoryItem 
              key={child.id} 
              category={child} 
              locale={locale}
              level={level + 1}
              onCategorySelect={onCategorySelect}
              expandedId={expandedId}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function CategorySelector({ categories, locale }: CategorySelectorProps) {
  const { t } = useT('main')
  const router = useRouter()
  const searchParams = useSearchParams()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  
  const topLevelCategories = categories
    .filter(cat => !cat.parent_category_id)
    .sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))
    
  const handleCategorySelect = (categoryId: string) => {
    const params = new URLSearchParams(searchParams)
    if (categoryId === 'oprema24') {
      params.delete('category')
    } else {
      params.set('category', categoryId)
    }
    params.set('page', '1')
    router.replace(`?${params.toString()}`, { scroll: false })
  }

  const handleToggleExpand = (categoryId: string) => {
    setExpandedId(expandedId === categoryId ? null : categoryId)
  }

  return (
    <>
      <div className="border-b border-gray-100">
        <button
          onClick={() => handleCategorySelect('oprema24')}
          className="flex items-center gap-2 p-3 hover:bg-gray-50 cursor-pointer w-full text-left"
        >
          <span className="text-xl font-semibold text-gray-900">Oprema 24</span>
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">



        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{t('categories')}</h2>
        </div>

        <div className="max-h-screen overflow-y-auto">
          {topLevelCategories.map((category) => (
            <CategoryItem
              key={category.id}
              category={category}
              locale={locale}
              onCategorySelect={handleCategorySelect}
              expandedId={expandedId}
              onToggleExpand={handleToggleExpand}
            />
          ))}
        </div>
      </div>

    </>
  )
}